/**
 * Product session for Web (V3 hybrid JWT standard).
 *
 * cookie mode (QA/Prod default): HttpOnly JWT on API host — JS never stores JWT or api_key.
 * CSRF token is memory-only. Cookie presence is discovered via credentials + /csrf|/users/me.
 * hybrid/dev: optional legacy api_key in sessionStorage only when matrix/env allows.
 */
import {
  ssoAllowLegacyApiKeyClient,
  ssoApiPath,
  ssoPreferCookieSession,
} from '../config/environments';

const SESSION_TOKEN_KEY = 'candor_session_token';
const SESSION_USER_KEY = 'candor_session_user';
const LEGACY_TOKEN_KEYS = ['candor_token', 'dashboard_token'];
const LEGACY_USER_KEY = 'candor_user';
/** Tab hint only — never the JWT. Prefer sessionStorage; localStorage cleared on cookie mark. */
const COOKIE_FLAG = 'candor_cookie_session';
/** IDE ticket handoff only: allow X-API-Key when third-party cookies are blocked. */
const IDE_HANDOFF_LEGACY_FLAG = 'candor_ide_handoff_legacy';

let memoryApiKey = '';
let memoryUser = null;
let memoryCsrf = '';
let migrated = false;

function safeJsonParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function looksLikeJwt(value) {
  const key = typeof value === 'string' ? value.trim() : '';
  return key.split('.').length === 3 && key.length > 80;
}

/** True while an IDE→web ticket handoff is using a one-shot api_key fallback. */
export function hasIdeHandoffLegacySession() {
  try {
    return sessionStorage.getItem(IDE_HANDOFF_LEGACY_FLAG) === '1';
  } catch (_) {
    return false;
  }
}

export function clearIdeHandoffLegacySession() {
  try {
    sessionStorage.removeItem(IDE_HANDOFF_LEGACY_FLAG);
  } catch (_) {
    /* ignore */
  }
}

function markIdeHandoffLegacySession(on) {
  try {
    if (on) {
      sessionStorage.setItem(IDE_HANDOFF_LEGACY_FLAG, '1');
    } else {
      sessionStorage.removeItem(IDE_HANDOFF_LEGACY_FLAG);
    }
  } catch (_) {
    /* ignore */
  }
}

function wipeClientApiKeyMaterial() {
  memoryApiKey = '';
  try {
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
  } catch (_) {
    /* ignore */
  }
  LEGACY_TOKEN_KEYS.forEach((k) => {
    try {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    } catch (_) {
      /* ignore */
    }
  });
}

/** Strip secrets before any durable user profile write. */
export function sanitizeUserProfile(userData) {
  if (!userData || typeof userData !== 'object') return null;
  const safe = { ...userData };
  delete safe.api_key;
  delete safe.password;
  delete safe.access_token;
  delete safe.refresh_token;
  delete safe.id_token;
  delete safe.session_token;
  delete safe.csrf_token;
  delete safe.token;
  return safe;
}

function wipeLegacyLocalTokens() {
  LEGACY_TOKEN_KEYS.forEach((k) => {
    try {
      localStorage.removeItem(k);
    } catch (_) {
      /* ignore */
    }
  });
  try {
    const u = safeJsonParse(localStorage.getItem(LEGACY_USER_KEY));
    if (u && (u.api_key || u.password || u.access_token || u.csrf_token)) {
      localStorage.setItem(LEGACY_USER_KEY, JSON.stringify(sanitizeUserProfile(u)));
    }
  } catch (_) {
    /* ignore */
  }
}

/** One-time: lift legacy storage, then enforce cookie-mode purge. */
export function migrateLegacyAuthStorage() {
  if (migrated || typeof window === 'undefined') return;
  migrated = true;

  try {
    // ***** Code Start BH17082026_1055 - Cookie mode never rehydrates api_key
    if (!ssoAllowLegacyApiKeyClient() || ssoPreferCookieSession() || hasCookieSession()) {
      wipeClientApiKeyMaterial();
      wipeLegacyLocalTokens();
      if (!memoryUser) {
        memoryUser =
          sanitizeUserProfile(safeJsonParse(sessionStorage.getItem(SESSION_USER_KEY))) ||
          sanitizeUserProfile(safeJsonParse(localStorage.getItem(LEGACY_USER_KEY)));
      }
      return;
    }
    // ***** Code End BH17082026_1055

    if (!memoryApiKey) {
      const fromSession = (sessionStorage.getItem(SESSION_TOKEN_KEY) || '').trim();
      if (fromSession && !looksLikeJwt(fromSession)) {
        memoryApiKey = fromSession;
      }
    }
    if (!memoryApiKey) {
      for (const k of LEGACY_TOKEN_KEYS) {
        const v = (localStorage.getItem(k) || '').trim();
        if (v && !looksLikeJwt(v)) {
          memoryApiKey = v;
          break;
        }
      }
    }
    if (!memoryUser) {
      memoryUser =
        sanitizeUserProfile(safeJsonParse(sessionStorage.getItem(SESSION_USER_KEY))) ||
        sanitizeUserProfile(safeJsonParse(localStorage.getItem(LEGACY_USER_KEY)));
    }
    if (memoryApiKey) {
      try {
        sessionStorage.setItem(SESSION_TOKEN_KEY, memoryApiKey);
      } catch (_) {
        /* private mode — memory only */
      }
    }
    if (memoryUser) {
      try {
        sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(memoryUser));
        localStorage.setItem(LEGACY_USER_KEY, JSON.stringify(memoryUser));
      } catch (_) {
        /* ignore */
      }
    }
  } finally {
    wipeLegacyLocalTokens();
  }
}

export function getAuthApiKey() {
  migrateLegacyAuthStorage();
  // ***** Code Start BH17082026_1055 - Never expose api_key when cookie JWT is active
  // IDE handoff legacy flag: third-party cookies blocked; allow stored ticket key.
  if (hasCookieSession() || (ssoPreferCookieSession() && !hasIdeHandoffLegacySession())) {
    if (memoryApiKey || typeof sessionStorage !== 'undefined') {
      wipeClientApiKeyMaterial();
    }
    return '';
  }
  // ***** Code End BH17082026_1055
  if (!ssoAllowLegacyApiKeyClient() && !hasIdeHandoffLegacySession()) return '';
  if (memoryApiKey) return memoryApiKey;
  try {
    const fromSession = (sessionStorage.getItem(SESSION_TOKEN_KEY) || '').trim();
    if (fromSession && !looksLikeJwt(fromSession)) {
      memoryApiKey = fromSession;
      return memoryApiKey;
    }
  } catch (_) {
    /* ignore */
  }
  return '';
}

export function getAuthUser() {
  migrateLegacyAuthStorage();
  if (memoryUser) return memoryUser;
  try {
    const fromSession = sanitizeUserProfile(safeJsonParse(sessionStorage.getItem(SESSION_USER_KEY)));
    if (fromSession) {
      memoryUser = fromSession;
      return memoryUser;
    }
  } catch (_) {
    /* ignore */
  }
  try {
    const fromLocal = sanitizeUserProfile(safeJsonParse(localStorage.getItem(LEGACY_USER_KEY)));
    if (fromLocal) {
      memoryUser = fromLocal;
      return memoryUser;
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

export function setAuthSession(apiKey, userData) {
  const key = typeof apiKey === 'string' ? apiKey.trim() : '';
  // Never persist a JWT in the browser (cookie session).
  const looksJwt = looksLikeJwt(key);
  const allowKey =
    !looksJwt &&
    Boolean(key) &&
    !hasCookieSession() &&
    ((ssoAllowLegacyApiKeyClient() && !ssoPreferCookieSession()) ||
      hasIdeHandoffLegacySession());
  memoryApiKey = allowKey ? key : '';
  memoryUser = sanitizeUserProfile(userData);

  try {
    if (memoryApiKey) {
      sessionStorage.setItem(SESSION_TOKEN_KEY, memoryApiKey);
    } else {
      sessionStorage.removeItem(SESSION_TOKEN_KEY);
    }
  } catch (_) {
    /* memory only */
  }

  try {
    if (memoryUser) {
      const raw = JSON.stringify(memoryUser);
      sessionStorage.setItem(SESSION_USER_KEY, raw);
      // Non-secret profile may stay in localStorage for UI convenience
      localStorage.setItem(LEGACY_USER_KEY, raw);
    }
  } catch (_) {
    /* ignore */
  }

  wipeLegacyLocalTokens();
  return { apiKey: memoryApiKey, user: memoryUser };
}

/**
 * IDE → web ticket handoff only: store session_token when HttpOnly cookie cannot
 * be established (cross-site SPA). Cleared on normal cookie login / logout.
 */
export function setIdeHandoffLegacySession(apiKey, userData) {
  const key = typeof apiKey === 'string' ? apiKey.trim() : '';
  if (!key || looksLikeJwt(key)) {
    return { apiKey: '', user: sanitizeUserProfile(userData) };
  }
  markCookieSession(false);
  markIdeHandoffLegacySession(true);
  return setAuthSession(key, userData);
}

export function setAuthUserProfile(userData) {
  if (!userData) {
    memoryUser = null;
    try {
      sessionStorage.removeItem(SESSION_USER_KEY);
      localStorage.removeItem(LEGACY_USER_KEY);
    } catch (_) {
      /* ignore */
    }
    return null;
  }
  memoryUser = sanitizeUserProfile(userData);
  try {
    if (memoryUser) {
      const raw = JSON.stringify(memoryUser);
      sessionStorage.setItem(SESSION_USER_KEY, raw);
      localStorage.setItem(LEGACY_USER_KEY, raw);
    }
  } catch (_) {
    /* ignore */
  }
  return memoryUser;
}

export function clearAuthSession() {
  memoryApiKey = '';
  memoryUser = null;
  memoryCsrf = '';
  clearIdeHandoffLegacySession();
  try {
    sessionStorage.removeItem(COOKIE_FLAG);
    localStorage.removeItem(COOKIE_FLAG);
  } catch (_) {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
    sessionStorage.removeItem(SESSION_USER_KEY);
  } catch (_) {
    /* ignore */
  }
  wipeLegacyLocalTokens();
  try {
    localStorage.removeItem(LEGACY_USER_KEY);
  } catch (_) {
    /* ignore */
  }
}

/** True if this tab has a product session (cookie flag or legacy api_key). */
export function hasAuthApiKey() {
  return Boolean(getAuthApiKey()) || hasCookieSession();
}

export function setCsrfToken(token) {
  memoryCsrf = typeof token === 'string' ? token.trim() : '';
}

export function getCsrfToken() {
  return memoryCsrf;
}

export function markCookieSession(on) {
  try {
    if (on) {
      // ***** Code Start BH17082026_1055 - Cookie session: wipe api_key; flag is session-only hint
      clearIdeHandoffLegacySession();
      wipeClientApiKeyMaterial();
      sessionStorage.setItem(COOKIE_FLAG, '1');
      // Do not persist cookie flag in localStorage (avoids durable fake-auth without JWT cookie)
      localStorage.removeItem(COOKIE_FLAG);
      // ***** Code End BH17082026_1055
    } else {
      sessionStorage.removeItem(COOKIE_FLAG);
      localStorage.removeItem(COOKIE_FLAG);
    }
  } catch (_) {
    /* ignore */
  }
}

export function hasCookieSession() {
  try {
    if (sessionStorage.getItem(COOKIE_FLAG) === '1') return true;
    // Migrate away from older durable flag
    if (localStorage.getItem(COOKIE_FLAG) === '1') {
      sessionStorage.setItem(COOKIE_FLAG, '1');
      localStorage.removeItem(COOKIE_FLAG);
      wipeClientApiKeyMaterial();
      return true;
    }
  } catch (_) {
    /* ignore */
  }
  return false;
}

/** Axios/fetch helpers for the LLM API (cross-origin cookie + optional CSRF). */
export function candorWebRequestAuth(extraHeaders = {}) {
  const headers = { Accept: 'application/json', ...extraHeaders };
  // ***** Code Start BH17082026_1055 - Cookie JWT path never sends X-API-Key
  const cookieMode =
    hasCookieSession() ||
    (ssoPreferCookieSession() && !hasIdeHandoffLegacySession());
  if (cookieMode) {
    wipeClientApiKeyMaterial();
  } else {
    const key = getAuthApiKey();
    if (key && (ssoAllowLegacyApiKeyClient() || hasIdeHandoffLegacySession())) {
      headers['X-API-Key'] = key;
    }
  }
  if (memoryCsrf) {
    headers['X-CSRF-Token'] = memoryCsrf;
  }
  // ***** Code End BH17082026_1055
  return { headers, credentials: 'include' };
}

/** After reload, CSRF is gone from memory — refill from the HttpOnly cookie JWT. */
export async function refreshCsrfFromCookie() {
  try {
    const { apiFetch, API_JSON_ACCEPT, isApiMisrouteResponse } = await import('./apiBase');
    // ***** Code Start BH28082026_1115 - CSRF probe with SPA misroute recovery
    const res = await apiFetch(ssoApiPath('csrf'), {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: API_JSON_ACCEPT },
    });
    let raw = '';
    try {
      raw = await res.clone().text();
    } catch (_) {
      raw = '';
    }
    if (isApiMisrouteResponse(res, raw)) return false;
    if (res.status >= 500) return false;
    if (!res.ok) return false;
    const data = raw ? JSON.parse(raw) : {};
    // ***** Code End BH28082026_1115
    if (typeof data.csrf_token === 'string' && data.csrf_token) {
      setCsrfToken(data.csrf_token);
      markCookieSession(true);
      return true;
    }
  } catch (_) {
    /* stay without CSRF; mutating calls will 403 */
  }
  return false;
}

function apiUnreachableError() {
  const err = new Error('api_unreachable');
  err.code = 'api_unreachable';
  return err;
}

/**
 * Discover HttpOnly cookie session after reload/new tab.
 * Probe /csrf first (200 when signed out) — never GET /users/me until a cookie CSRF exists.
 */
export async function restoreCookieSessionProfile() {
  try {
    const csrfOk = await refreshCsrfFromCookie();
    if (!csrfOk) return null;
    const { apiFetch, API_JSON_ACCEPT, isApiMisrouteResponse } = await import('./apiBase');
    // ***** Code Start BH28082026_1115 - /users/me with SPA misroute recovery
    const me = await apiFetch('/api/v1/users/me', {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: API_JSON_ACCEPT },
    });
    let raw = '';
    try {
      raw = await me.clone().text();
    } catch (_) {
      raw = '';
    }
    if (isApiMisrouteResponse(me, raw)) return null;
    if (!me.ok) return null;
    const profile = raw ? JSON.parse(raw) : {};
    // ***** Code End BH28082026_1115
    if (!profile || !profile.email) return null;
    markCookieSession(true);
    setAuthUserProfile(profile);
    return profile;
  } catch (_) {
    return null;
  }
}

/**
 * After password/SSO login JSON says cookie session, prove the HttpOnly JWT
 * actually arrived (cross-site XHR drops SameSite=Lax). Never mark signed-in
 * until /users/me succeeds — otherwise subscription calls 401 and wipe the UI.
 *
 * Throws Error with code 'api_unreachable' when IIS/ARR returns 502 (no CORS
 * headers) or the browser cannot reach the API at all.
 */
export async function establishCookieSession() {
  try {
    const { apiFetch, API_JSON_ACCEPT, isApiMisrouteResponse } = await import('./apiBase');
    // ***** Code Start BH28082026_1115 - Cookie establish with SPA misroute recovery
    const csrfRes = await apiFetch(ssoApiPath('csrf'), {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: API_JSON_ACCEPT },
    });
    let csrfRaw = '';
    try {
      csrfRaw = await csrfRes.clone().text();
    } catch (_) {
      csrfRaw = '';
    }
    if (isApiMisrouteResponse(csrfRes, csrfRaw) || csrfRes.status >= 500) {
      markCookieSession(false);
      throw apiUnreachableError();
    }
    let csrfData = {};
    try {
      csrfData = csrfRaw ? JSON.parse(csrfRaw) : {};
    } catch (_) {
      csrfData = {};
    }
    if (typeof csrfData.csrf_token === 'string' && csrfData.csrf_token) {
      setCsrfToken(csrfData.csrf_token);
    }
    const me = await apiFetch('/api/v1/users/me', {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: API_JSON_ACCEPT },
    });
    let meRaw = '';
    try {
      meRaw = await me.clone().text();
    } catch (_) {
      meRaw = '';
    }
    if (isApiMisrouteResponse(me, meRaw) || me.status >= 500) {
      markCookieSession(false);
      throw apiUnreachableError();
    }
    if (!me.ok) {
      markCookieSession(false);
      return null;
    }
    const profile = meRaw ? JSON.parse(meRaw) : {};
    // ***** Code End BH28082026_1115
    if (!profile || !profile.email) {
      markCookieSession(false);
      return null;
    }
    markCookieSession(true);
    setAuthUserProfile(profile);
    return profile;
  } catch (e) {
    markCookieSession(false);
    if (e && e.code === 'api_unreachable') throw e;
    throw apiUnreachableError();
  }
}
